/**
 * Batch 27h — Suspend-tenant dialog (reason required).
 *
 * Per spec L1411 — suspend requires a reason note. Pydantic enforces
 * min_length=1 + str_strip_whitespace; the frontend mirrors so users see
 * the friction client-side rather than as a 422.
 *
 * 27h does NOT cancel the Stripe subscription (OQ-2 deferred per design §3.2);
 * the dialog explainer surfaces the consequence so operators don't think
 * billing was canceled.
 */
import { useState } from "react";
import {
  useSuspendTenant,
  type TenantDetail,
} from "@/hooks/queries/admin/useAdminTenants";
import { useToastStore } from "@/stores/useToastStore";

interface Props {
  tenant: TenantDetail;
  onClose: () => void;
}

export function SuspendTenantDialog({ tenant, onClose }: Props): JSX.Element {
  const [reason, setReason] = useState("");
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const suspend = useSuspendTenant(tenant.id);
  const addToast = useToastStore((s) => s.addToast);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrMsg(null);
    const trimmed = reason.trim();
    if (trimmed.length === 0) {
      setErrMsg("Reason is required.");
      return;
    }
    suspend.mutate(
      { reason: trimmed },
      {
        onSuccess: () => {
          addToast(`Tenant suspended: ${tenant.name}`, "success");
          onClose();
        },
        onError: (err) => {
          setErrMsg(err.message || "Could not suspend tenant.");
        },
      },
    );
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <form
        onSubmit={submit}
        className="w-full max-w-md space-y-3 rounded-xl border border-[var(--p-slate-150)] bg-white p-5 shadow-card dark:border-white/[0.06] dark:bg-surface-card dark:shadow-card-dark"
      >
        <h2 className="text-base font-semibold text-text-primary dark:text-text-primary">
          Suspend {tenant.name}?
        </h2>
        <p className="text-sm text-text-secondary dark:text-brand-100/70">
          All users will be immediately blocked from logging in. Existing
          data is retained. Stripe subscription is{" "}
          <strong className="text-text-primary dark:text-text-primary">not</strong>{" "}
          canceled at suspension in this release; reconcile billing manually
          if needed. You can reinstate at any time.
        </p>
        <label className="block text-sm">
          <span className="block text-text-secondary dark:text-brand-100/70">
            Reason (required)
          </span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
            maxLength={1024}
            rows={3}
            className="mt-1 w-full rounded-md border border-[var(--p-slate-150)] bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:bg-surface-canvas dark:text-text-primary dark:focus:ring-brand-teal"
            placeholder="e.g. ToS violation under triage; ticket-1234"
          />
        </label>
        {errMsg ? (
          <p className="text-sm text-red-600 dark:text-red-400">{errMsg}</p>
        ) : null}
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-brand-200 px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-surface-canvas dark:border-white/[0.08] dark:text-brand-100/80 dark:hover:bg-white/[0.04]"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={suspend.isPending}
            className="rounded bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
          >
            {suspend.isPending ? "Suspending…" : "Suspend"}
          </button>
        </div>
      </form>
    </div>
  );
}
